import { Controller } from "@hotwired/stimulus"

// The notes layer (application/_flash): each note pops up over the page, over an open dialog too, says its one line
// and goes. A done note goes after a few seconds, but not while the pointer or focus is on it; a refused one stays
// until it's closed. The server's notes come in the page; scripts send more with lib/toast.js.
//
// A modal dialog makes everything outside it inert, popovers too, so while one is open the layer moves inside it, and
// back when it closes, or when the page takes the dialog away without closing it (React does), which fires nothing, so
// the page is watched while the layer is away. The layer listens for its own clicks, as it isn't always inside this
// controller. The page's root says the layer is up, so lib/toast.js finds it wherever it is.
const LINGER = 5000
const MOST = 3

export default class extends Controller {
  static targets = ["done", "refused", "layer"]

  connect() {
    this.layer = this.layerTarget
    this.home = this.layer.parentElement
    this.timers = new Map()
    this.actions = new Map()
    this.listen()
    // What the page came with is said again once it's up, so a screen reader hears it.
    this.notes().forEach((note) => this.start(note, true))
    this.layer.dataset.ready = ""
    document.documentElement.dataset.toasts = ""
    const waiting = window.locotapToasts || []
    window.locotapToasts = []
    waiting.forEach((detail) => this.add({ detail }))
  }

  disconnect() {
    this.timers.forEach(clearTimeout)
    this.quiet?.abort()
    this.away?.disconnect()
    delete document.documentElement.dataset.toasts
    if (this.layer.parentElement !== this.home) this.home.append(this.layer)
  }

  listen() {
    this.quiet = new AbortController()
    const signal = this.quiet.signal
    const on = (target, type, run, capture = false) => target.addEventListener(type, run, { signal, capture })
    const note = (event) => event.target.closest?.(".toast")
    on(this.layer, "click", (event) => {
      if (event.target.closest(".toast-act")) this.act(note(event))
      else if (event.target.closest(".toast-x")) this.drop(note(event))
    })
    on(this.layer, "mouseover", (event) => this.hold(note(event)))
    on(this.layer, "focusin", (event) => this.hold(note(event)))
    on(this.layer, "mouseout", (event) => this.go(note(event)))
    on(this.layer, "focusout", (event) => this.go(note(event)))
    // A dialog opening or closing under a note it's showing: the note follows it. With nothing showing, the layer
    // just goes home.
    on(document, "toggle", (event) => { if (event.target.tagName === "DIALOG") this.settle() }, true)
    on(document, "close", (event) => { if (event.target.tagName === "DIALOG") this.settle() }, true)
  }

  settle() {
    if (this.notes().length) this.lift()
    else this.goHome()
  }

  goHome() {
    this.watchAway(false)
    if (this.layer.parentElement === this.home) return
    try { this.layer.hidePopover() } catch {}
    this.home.append(this.layer)
  }

  // While the layer sits in a dialog, the page is watched for that dialog leaving it.
  watchAway(away) {
    if (!away) return this.away?.disconnect()
    this.away ||= new MutationObserver(() => { if (!this.layer.isConnected) this.settle() })
    this.away.observe(document.body, { childList: true, subtree: true })
  }

  notes() {
    return [...this.layer.querySelectorAll(".toast:not(.is-going)")]
  }

  add({ detail: { text, tone = "done", action, linger } }) {
    const note = (tone === "refused" ? this.refusedTarget : this.doneTarget).content.firstElementChild.cloneNode(true)
    note.querySelector(".toast-text").textContent = text
    if (action) {
      const button = note.querySelector(".toast-act")
      button.textContent = action.label
      button.hidden = false
      this.actions.set(note, action.run)
    }
    if (linger) note.dataset.linger = linger
    // The same note again (a button pressed twice, say) takes the old one's place rather than stacking under it.
    this.notes().filter((old) => old.querySelector(".toast-text").textContent === text).forEach((old) => this.drop(old))
    this.layer.prepend(note)
    this.start(note)
  }

  start(note, again = false) {
    this.lift()
    if (again) {
      const line = note.querySelector(".toast-text")
      const text = line.textContent
      line.textContent = ""
      requestAnimationFrame(() => { line.textContent = text })
    }
    this.notes().slice(MOST).forEach((old) => this.drop(old))
    this.wait(note)
  }

  // Into the open modal dialog, if there is one, and lifted into the top layer again, so it's over anything opened
  // since.
  lift() {
    const dialog = [...document.querySelectorAll("dialog[open]")].reverse().find((open) => open.matches(":modal"))
    const place = dialog || this.home
    try {
      if (this.layer.matches(":popover-open")) this.layer.hidePopover()
      if (this.layer.parentElement !== place) place.append(this.layer)
      this.layer.showPopover()
    } catch {
      // Without popovers it's a note fixed over the page.
      if (this.layer.parentElement !== place) place.append(this.layer)
    }
    this.watchAway(place !== this.home)
  }

  wait(note) {
    clearTimeout(this.timers.get(note))
    if (note.classList.contains("is-refused")) return
    this.timers.set(note, setTimeout(() => this.drop(note), Number(note.dataset.linger) || LINGER))
  }

  hold(note) {
    if (note) clearTimeout(this.timers.get(note))
  }

  go(note) {
    if (!note) return
    requestAnimationFrame(() => {
      if (note.isConnected && !note.matches(":hover, :focus-within")) this.wait(note)
    })
  }

  act(note) {
    this.actions.get(note)?.()
    this.drop(note)
  }

  drop(note) {
    if (!note || note.classList.contains("is-going")) return
    clearTimeout(this.timers.get(note))
    this.timers.delete(note)
    this.actions.delete(note)
    note.classList.add("is-going")
    setTimeout(() => {
      note.remove()
      if (!this.notes().length) try { this.layer.hidePopover() } catch {}
    }, 180)
  }

  // Not kept in the page Turbo shows on Back.
  clear() {
    this.notes().forEach((note) => note.remove())
    this.goHome()
  }
}
